// ibook-ui/src/components/DialogLegalForm/DialogLegalForm.tsx
import { Autocomplete, Box, Button, DialogActions, DialogContent, DialogTitle, Grid, InputAdornment, MenuItem, Radio, RadioProps, Select, SelectChangeEvent, TextField, Typography, styled } from '@mui/material';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector, } from "../../store/hooks";
// import styles from './DialogAddressForm.module.css';

import CustomTextField from '../FormFields/CustomTextField';
import { updateLegalData } from '../../store/accountThunk';
import CustomSelect, { IOptions } from '../FormFields/CustomSelect';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import LoadingButton from '@mui/lab/LoadingButton';
import CustomCheckbox from '../FormFields/CustomCheckbox';
import { ApiStatus } from "../../utils/api.types";
import { makeStyles } from '@material-ui/core';


const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  transition: '0ms',
  width: 650,
  borderRadius: 2,
  bgcolor: 'background.paper',
  border: 'none',
  boxShadow: 24,
  padding: 0
};

const useStyles = makeStyles({
  paper: {
    border: "1.5px solid #e9ecf1",
    width: '108%',
    boxShadow: 'none',
    marginLeft: '-6px',
    borderRadius: 7
  }
});


const BpIcon = styled('span')(({ theme }) => ({
  borderRadius: '50%',
  width: 16,
  height: 16,
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 0 0 1px rgb(16 22 26 / 40%)'
      : 'inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)',
  backgroundColor: theme.palette.mode === 'dark' ? '#394b59' : '#f5f8fa',
  backgroundImage:
    theme.palette.mode === 'dark'
      ? 'linear-gradient(180deg,hsla(0,0%,100%,.05),hsla(0,0%,100%,0))'
      : 'linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))',
  '.Mui-focusVisible &': {
    outline: '2px auto rgba(19,124,189,.6)',
    outlineOffset: 2,
  },
  'input:hover ~ &': {
    backgroundColor: theme.palette.mode === 'dark' ? '#30404d' : '#ebf1f5',
  },
  'input:disabled ~ &': {
    boxShadow: 'none',
    background:
      theme.palette.mode === 'dark' ? 'rgba(57,75,89,.5)' : 'rgba(206,217,224,.5)',
  },
}));

const BpCheckedIcon = styled(BpIcon)({
  backgroundColor: '#137cbd',
  backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
  '&:before': {
    display: 'block',
    width: 16,
    height: 16,
    backgroundImage: 'radial-gradient(#fff,#fff 28%,transparent 32%)',
    content: '""',
  },
  'input:hover ~ &': {
    backgroundColor: '#106ba3',
  },
});

// Inspired by blueprintjs
function BpRadio(props: RadioProps) {
  return (
    <Radio
      disableRipple
      color="default"
      checkedIcon={<BpCheckedIcon />}
      icon={<BpIcon />}
      {...props}
    />
  );
}

interface DialogLegalFormProps {
  open: boolean;
  data: any;
  onClose: (k: any) => void;
}

const DialogLegalForm: FC<DialogLegalFormProps> = ({ onClose, open, children, data }) => {

  const dispatch = useAppDispatch();

  const [countryOptions, setCountryOptions] = useState<IOptions[]>([]);
  const { accountDetails, status } = useAppSelector(state => state.account);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const countriesList = useAppSelector(state => state.country.countries);
  const [country, setCountry] = useState('');
  const [isSME, setIsSME] = useState(true);

  const formRef = useRef<any>();


  // Schema to validate account form.
  const schema = yup.object().shape({
    // registrationNo: yup.string().required("Registration is required."),
    countryOfRegistration: yup.string().required("Country of registration is required."),
    stateOfRegistration: yup.string().required("State of registration is required."),
  });

  const methods = useForm<any>({ mode: 'onTouched', resolver: yupResolver(schema) });

  const onSubmit = (e: any) => {
    dispatch(updateLegalData(
      {
        legalPayload: {
          ...methods.getValues()
        }, id: accountDetails._id
      }));
    setFormSubmitted(true);
  }

  const classes = useStyles();

  useEffect(() => {
    const setInitialData = () => {
      methods.setValue('legalName', data.legalName);
      methods.setValue('registrationNo', data.registrationNo);
      methods.setValue('countryOfRegistration', data.countryOfRegistration?._id || '');
      methods.setValue('stateOfRegistration', data.stateOfRegistration);
      methods.setValue('taxIdentificationNumber1', data.taxIdentificationNumber1);
      methods.setValue('taxIdentificationNumber2', data.taxIdentificationNumber2);
      methods.setValue('companyType', data.companyType);
      methods.setValue('smeRegistrationFlag', !!data.smeRegistrationFlag); // this shit does not work

      setIsSME(!!data.smeRegistrationFlag);
      setCountry(data?.countryOfRegistration?._id)
    }

    setInitialData();

    setCountryOptions(countriesList.map((c: any) => {
      return { label: c.name, value: c._id }
    }));
  }, [data, countriesList, methods]);

  useEffect(() => {
    if (status === ApiStatus.SUCCESS && formSubmitted) {
      onClose(false);
      setFormSubmitted(false);
    }
  }, [status, formSubmitted, onClose]);

  return (
    <FormProvider {...methods}>
      <Box sx={style} component="form" onSubmit={methods.handleSubmit(data => onSubmit(data))} ref={formRef} noValidate>
        <Grid container >
          <Grid {...{ xs: 12, sm: 8, md: 4, lg: 12, p: 2, pl: 2, bgcolor: '#e9ecf1' }} style={{ borderTopLeftRadius: 8, borderTopRightRadius: 8, height: 50 }} >
            <div style={{ float: 'right', marginTop: -7, marginRight: -5 }}>
              <LoadingButton
                type="submit"
                name={'submit'}
                // disabled={!methods.formState.isValid}
                loading={status === ApiStatus.LOADING}
                style={{ color: '#488dff', backgroundColor: '#fff', border: '1px solid #488dff', marginRight: 10 }}
                variant="contained"
                size='small'>
                {`Save`}
              </LoadingButton>

              <Button
                style={{ color: '#488dff', backgroundColor: '#fff', border: '1px solid #488dff', marginRight: 10 }}
                variant="contained"
                size='small'
                onClick={() => {
                  methods.reset();
                  onClose(false);
                }} >
                Cancel
              </Button>
            </div>
            <h3 style={{ marginTop: -2 }}>Registration &#38; Legal Update</h3>
          </Grid>

        </Grid>

        <Grid container p={2} spacing={2}>
          <Grid item xs={6} pr={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Legal Name</h5>
            <TextField placeholder="Enter Legal Name" required size="small"
              {...methods.register('legalName')}
              name="legalName"
              className='c-text-field-4'
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              InputProps={{
                disableUnderline: true,
                // endAdornment:<InputAdornment position="start" style={{marginTop:-5}}>{`${(selectedInventory.sale && selectedInventory.sale.unit) ? selectedInventory.sale.unit.code : ''}`}</InputAdornment>
              }}
            />
            <p style={{ color: "#c33b3b" }}>{methods.formState.errors.qty?.message ?? ""}</p>
          </Grid>

          <Grid item xs={6} pl={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Registration Number</h5>
            <TextField placeholder="Enter Registration Number" required size="small" {...methods.register('registrationNo')}
              className='c-text-field-4'
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              InputProps={{
                disableUnderline: true,
                // endAdornment:<InputAdornment position="start" style={{marginTop:-5}}>{`${(selectedInventory.sale && selectedInventory.sale.unit) ? selectedInventory.sale.unit.code : ''}`}</InputAdornment>
              }}
            />
            <p style={{ color: "#c33b3b" }}>{methods.formState.errors.qty?.message ?? ""}</p>
          </Grid>

          <Grid item xs={6} pr={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Country of registration*</h5>

            <Select
              className='c-text-field-4'
              disableUnderline
              MenuProps={{
                PaperProps: {
                  sx: {
                    border: "1.5px solid #e9ecf1",
                    // width: 255,
                    // marginLeft:'-29px !important',
                    boxShadow: 'none',
                    borderRadius: '5px'
                  }
                }
              }}
              variant='outlined'
              sx={{
                width: '100%',
                boxShadow: "none",
                paddingTop: '0px !important',
                borderRadius: '7px !important',
                marginTop: '4px',
                // fontSize:12,
                '& .MuiOutlinedInput-input': { p: 0.7 },
                // '& .MuiSelect-select': { p:0 },
                marginBottom: '3px',
                ".MuiOutlinedInput-notchedOutline": { border: 0 },
                "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                {
                  border: 0,
                },
                "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                {
                  border: 0,
                },
              }}
              value={country ? country : ''}
              onChange={(event: SelectChangeEvent<any>) => {
                setCountry(event.target.value);
              }}
            >
              <MenuItem value={0} key={0}>Select Relation</MenuItem>
              {countryOptions.map(option => (
                <MenuItem
                  value={option.value}
                  key={option.value}
                  disabled={option.disable || false}
                >
                  {option.label}
                </MenuItem>
              ))
              }
            </Select>
          </Grid>

          <Grid item xs={6} pl={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>State Of Registration*</h5>
            <TextField placeholder="Enter State Of Registration*" required size="small" {...methods.register('stateOfRegistration')}
              className='c-text-field-4'
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              InputProps={{
                disableUnderline: true,
                // endAdornment:<InputAdornment position="start" style={{marginTop:-5}}>{`${(selectedInventory.sale && selectedInventory.sale.unit) ? selectedInventory.sale.unit.code : ''}`}</InputAdornment>
              }}
            />
            <p style={{ color: "#c33b3b" }}>{methods.formState.errors.qty?.message ?? ""}</p>
          </Grid>

          <Grid item xs={6} pr={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Tax Identification #1 (PAN for India)</h5>
            <TextField placeholder="Enter Tax Identification #1 (PAN for India)" required size="small" {...methods.register('taxIdentificationNumber1')}
              className='c-text-field-4'
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              InputProps={{
                disableUnderline: true,
                // endAdornment:<InputAdornment position="start" style={{marginTop:-5}}>{`${(selectedInventory.sale && selectedInventory.sale.unit) ? selectedInventory.sale.unit.code : ''}`}</InputAdornment>
              }}
            />
            <p style={{ color: "#c33b3b" }}>{methods.formState.errors.qty?.message ?? ""}</p>
          </Grid>

          <Grid item xs={6} pl={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Tax Identification #2 (GST for India)</h5>
            <TextField placeholder="Enter Tax Identification #2 (GST for India)" required size="small" {...methods.register('taxIdentificationNumber2')}
              className='c-text-field-4'
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              InputProps={{
                disableUnderline: true,
                // endAdornment:<InputAdornment position="start" style={{marginTop:-5}}>{`${(selectedInventory.sale && selectedInventory.sale.unit) ? selectedInventory.sale.unit.code : ''}`}</InputAdornment>
              }}
            />
            <p style={{ color: "#c33b3b" }}>{methods.formState.errors.qty?.message ?? ""}</p>
          </Grid>

          <Grid item xs={6} pr={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Company Type</h5>
            <TextField placeholder="Enter Company Type" required size="small" {...methods.register('companyType')}
              className='c-text-field-4'
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              InputProps={{
                disableUnderline: true,
                // endAdornment:<InputAdornment position="start" style={{marginTop:-5}}>{`${(selectedInventory.sale && selectedInventory.sale.unit) ? selectedInventory.sale.unit.code : ''}`}</InputAdornment>
              }}
            />
            <p style={{ color: "#c33b3b" }}>{methods.formState.errors.qty?.message ?? ""}</p>
          </Grid>

          <Grid item xs={6} pl={1} >
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>SME Registration</h5>
            <div style={{ marginLeft: -5 }}>
              <BpRadio checked={isSME === true} onChange={() => {
                setIsSME(true);
              }} size="small" style={{ marginRight: -3 }} />
              <label onClick={() => {
                setIsSME(true);
              }}>Yes</label>
              <BpRadio checked={isSME === false} onChange={() => {
                setIsSME(false);
              }} size="small" style={{ marginRight: -3, marginLeft: 5 }} />
              <label onClick={() => {
                setIsSME(false);
              }}>No</label>
            </div>
          </Grid>
        </Grid>
      </Box>

{/* 
      <DialogContent>
        <Grid container justifyContent="center" spacing={2} rowSpacing={-2}>
          <Grid item xs={12} md={12} sm={12} lg={12}>
            <CustomTextField label="Legal Name" name="legalName" variant="standard" />
          </Grid>
          <Grid item xs={12} md={12} sm={12} lg={12}>
            <CustomTextField label="Registration Number" name="registrationNo" variant="standard" />
          </Grid>

          <Grid item xs={12} md={12} sm={12} lg={12}>
            {countryOptions?.length && <CustomSelect
              name="countryOfRegistration"
              label="Country of registration*"
              id="country"
              required
              variant="standard"
              options={countryOptions}
              value={country ? country : ''}
              onChange={(event: SelectChangeEvent<any>) => {
                setCountry(event.target.value);
              }} />}
          </Grid>
          <Grid item xs={12} md={12} sm={12} lg={12}>
            <CustomTextField label="State Of Registration*" name="stateOfRegistration" variant="standard" />
          </Grid>
          <Grid item xs={12} md={12} sm={12} lg={12}>
            <CustomTextField label="Tax Identification #1 (PAN for India)" name="taxIdentificationNumber1" variant="standard" />
          </Grid><Grid item xs={12} md={12} sm={12} lg={12}>
            <CustomTextField label="Tax Identification #2 (GST for India)" name="taxIdentificationNumber2" variant="standard" />
          </Grid><Grid item xs={12} md={12} sm={12} lg={12}>
            <CustomTextField label="Company Type" name="companyType" variant="standard" />
          </Grid>

          <Grid item xs={12} md={12} sm={12} lg={12}>
            <CustomCheckbox
              label="SME Registration"
              name="smeRegistrationFlag"
              checked={isSME}
              onChange={(event, checked) => {
                setIsSME(checked);
              }}
            />
          </Grid>

        </Grid>
      </DialogContent> */}
    </FormProvider>
  );
};

export default DialogLegalForm;
